import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppIcon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';
import { cn } from '../../utils/cn';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'booking',
      title: 'طلب حجز جديد',
      message: 'لديك طلب حجز جديد من أحمد محمد لتصوير حفل زفاف',
      timestamp: '2024-01-15T10:30:00',
      isRead: false,
      icon: 'Calendar',
      iconColor: 'var(--color-primary)'
    },
    {
      id: 2,
      type: 'payment',
      title: 'تم استلام الدفعة',
      message: 'تم استلام دفعة بقيمة 2,500 ريال من حجز سابق',
      timestamp: '2024-01-14T15:20:00',
      isRead: false,
      icon: 'DollarSign',
      iconColor: 'var(--color-success)'
    },
    {
      id: 3,
      type: 'review',
      title: 'تقييم جديد',
      message: 'حصلت على تقييم 5 نجوم من فاطمة العتيبي',
      timestamp: '2024-01-13T09:15:00',
      isRead: true,
      icon: 'Star',
      iconColor: 'var(--color-warning)'
    },
    {
      id: 4,
      type: 'message',
      title: 'رسالة جديدة',
      message: 'لديك رسالة جديدة من خالد السعيد',
      timestamp: '2024-01-12T14:45:00',
      isRead: true,
      icon: 'MessageCircle',
      iconColor: 'var(--color-accent)'
    },
    {
      id: 5,
      type: 'system',
      title: 'تحديث النظام',
      message: 'تم إضافة ميزات جديدة للمنصة',
      timestamp: '2024-01-11T08:00:00',
      isRead: true,
      icon: 'Bell',
      iconColor: 'var(--color-muted-foreground)'
    }
  ]);

  const [filter, setFilter] = useState('all'); // all, unread, read

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length || 0;

  const handleMarkAsRead = (id) => {
    setNotifications(prev =>
      prev?.map(n => n?.id === id ? { ...n, isRead: true } : n)
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev =>
      prev?.map(n => ({ ...n, isRead: true }))
    );
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const filteredNotifications = notifications?.filter(n => {
    if (filter === 'unread') return !n?.isRead;
    if (filter === 'read') return n?.isRead;
    return true;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    return date?.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">الإشعارات</h1>
              <p className="text-muted-foreground">
                {unreadCount > 0 ? `لديك ${unreadCount} إشعار غير مقروء` : 'جميع الإشعارات مقروءة'}
              </p>
            </div>
            <BackButton to={-1} label="رجوع" className="px-4 py-2 rounded-lg border border-border hover:bg-muted" />
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'px-4 py-2 rounded-lg transition-all',
                filter === 'all' ?'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
              )}
            >
              الكل ({notifications?.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={cn(
                'px-4 py-2 rounded-lg transition-all',
                filter === 'unread' ?'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
              )}
            >
              غير مقروءة ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={cn(
                'px-4 py-2 rounded-lg transition-all',
                filter === 'read' ?'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
              )}
            >
              مقروءة ({notifications?.length - unreadCount})
            </button>
          </div>

          {/* Actions */}
          {notifications?.length > 0 && (
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-primary hover:underline"
                >
                  تعليم الكل كمقروء
                </button>
              )}
              <button
                onClick={handleClearAll}
                className="text-sm text-error hover:underline"
              >
                حذف الكل
              </button>
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications?.length === 0 ? (
            <div className="bg-card rounded-xl p-12 text-center border border-border">
              <AppIcon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">لا توجد إشعارات</h3>
              <p className="text-muted-foreground">
                {filter === 'unread' ? 'جميع الإشعارات مقروءة' : 'لا توجد إشعارات حالياً'}
              </p>
            </div>
          ) : (
            filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                onClick={() => handleMarkAsRead(notification?.id)}
                className={cn(
                  'bg-card rounded-xl p-5 border transition-all cursor-pointer',
                  !notification?.isRead
                    ? 'border-primary/30 bg-primary/5 hover:bg-primary/10' :'border-border hover:bg-muted/50'
                )}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${notification?.iconColor}15` }}
                  >
                    <AppIcon
                      name={notification?.icon}
                      size={24}
                      color={notification?.iconColor}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h3 className="font-semibold text-foreground">
                        {notification?.title}
                      </h3>
                      {!notification?.isRead && (
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {notification?.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTimestamp(notification?.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;